import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaGithub,
  FaWhatsapp,
  FaHeart,
} from "react-icons/fa";
import { SiLeetcode } from "react-icons/si";
import { FiMail, FiPhone, FiMapPin, FiArrowUpRight, FiArrowUp } from "react-icons/fi";
import "./Foot.css";

/* ─── Beautified drop-in for YOUR project's footer ─────────────
   Same sections data, same social links, same framer-motion
   entrances, same copyright logic — only the styling was rebuilt
   on the shared kk theme (inline styles → Foot.css).
   Extra garnish (no logic touched): "Let's work together" CTA,
   brand-glow social hovers, parallax watermark, back-to-top. */

/* one rAF loop for every [data-plx] inside the footer */
function useFooterParallax(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    let raf = 0;

    const render = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const r = el.getBoundingClientRect();
        if (r.bottom < -300 || r.top > vh + 300) continue;
        const y = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
}

export default function FooterKaranks1436() {
  const getYear = () => new Date().getFullYear();
  const rootRef = useRef(null);
  useFooterParallax(rootRef);

  // ─── Sections Data (UNCHANGED) ──────────────────────────────
  const sections = [
    {
      title: "Karanks1436",
      items: [
        { icon: <FiMapPin />, text: "Bathinda, India" },
        { icon: <FiMail />, text: "karanksxxx@gmail.com", link: "mailto:karanksxxx@gmail.com" },
        { icon: <FiPhone />, text: "+91 9041429065", link: "tel:9041429065" },
      ],
    },
    {
      title: "Quick Links",
      items: [
        { name: "Home", url: "/" },
        { name: "About", url: "/About" },
        { name: "Projects", url: "/Projects" },
        { name: "Gallery", url: "/Gallery" },
        { name: "Contact", url: "/Contact" },
      ],
    },
    {
      title: "Resources",
      items: [
        { name: "Notes", url: "/Note" },
        { name: "Codes", url: "/codeupload" },
        { name: "Videos", url: "/Video" },
        { name: "Blogs", url: "/Blogs" },
        { name: "Services", url: "/Services" },
        { name: "Products", url: "/products" },
      ],
    },
    {
      title: "Legal",
      items: [
        { name: "Terms", url: "/terms" },
        { name: "Privacy", url: "/privacy" },
        { name: "Contact", url: "/Contact" },
      ],
    },
  ];

  // Same 7 socials — only a brand accent added for the hover glow
  const socialIcons = [
    { icon: <FaFacebookF />, url: "https://www.facebook.com/profile.php?id=100070773074829", name: "Facebook", accent: "#1877f2" },
    { icon: <FaTwitter />, url: "https://x.com/Karanks1436", name: "Twitter", accent: "#e7e9ea" },
    { icon: <FaInstagram />, url: "https://www.instagram.com/karanks1436", name: "Instagram", accent: "#e1306c" },
    { icon: <FaYoutube />, url: "https://www.youtube.com/@Karanks1436", name: "YouTube", accent: "#ff0033" },
    { icon: <SiLeetcode />, url: "https://leetcode.com/u/karanks1436/", name: "LeetCode", accent: "#ffa116" },
    { icon: <FaGithub />, url: "https://github.com/Karanks1436", name: "GitHub", accent: "#e6e6e6" },
    { icon: <FaWhatsapp />, url: "https://wa.me/9041429065", name: "WhatsApp", accent: "#25d366" },
  ];

  // Back-to-top visibility (additive garnish — original logic untouched)
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <footer className="ft-foot" ref={rootRef}>
      {/* ═══════ background layers ═══════ */}
      <div className="ft-bg" aria-hidden="true">
        <span className="ft-grid-bg" data-plx="0.05" />
        <span className="ft-glow ft-glow-a" data-plx="0.12" />
        <span className="ft-glow ft-glow-b" data-plx="0.18" />
      </div>
      <span className="ft-beam" aria-hidden="true" />

      <div className="ft-container">
        {/* ═══════ header ═══════ */}
        <div className="ft-header">
          <motion.div
            className="ft-brand"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="ft-logo-row">
              <span className="ft-logo">Karanks1436</span>
              <span className="ft-status">
                <i aria-hidden="true" />
                Open to work
              </span>
            </div>

            <span className="ft-logo-sub">Full Stack Developer &amp; Designer</span>

            <h2 className="ft-title">
              Let&rsquo;s build
              <br />
              <span className="ft-serif">something worth shipping.</span>
            </h2>
          </motion.div>

          <motion.a
            className="ft-cta"
            href="/Contact"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.12 }}
          >
            <span className="ft-cta-dot" aria-hidden="true" />
            <span className="ft-cta-link">
              Let&apos;s work together <FiArrowUpRight />
            </span>
          </motion.a>
        </div>

        {/* ═══════ link grid ═══════ */}
        <div className="ft-grid">
          {sections.map((section, idx) => (
            <motion.div
              key={idx}
              className="ft-col"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.08, duration: 0.55 }}
            >
              <h4 className="ft-col-title">
                <span aria-hidden="true">{String(idx + 1).padStart(2, "0")}</span>
                {section.title}
              </h4>

              <ul className="ft-col-list">
                {section.items.map((item, i) => {
                  if (item.icon) {
                    return (
                      <li key={i} className="ft-info">
                        <span className="ft-info-icon">{item.icon}</span>
                        <span className="ft-info-text">
                          {item.link ? <a href={item.link}>{item.text}</a> : item.text}
                        </span>
                      </li>
                    );
                  }
                  return (
                    <li key={i}>
                      <a href={item.url} className="ft-link">
                        <span>{item.name}</span>
                        <FiArrowUpRight className="ft-link-arrow" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="ft-divider" aria-hidden="true" />

        {/* ═══════ bottom ═══════ */}
        <motion.div
          className="ft-bottom"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="ft-socials">
            {socialIcons.map((social, i) => (
              <motion.a
                key={i}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="ft-social"
                style={{ "--ft-accent": social.accent }}
                whileHover={{ scale: 1.1, y: -3 }}
                whileTap={{ scale: 0.95 }}
                title={social.name}
                aria-label={social.name}
              >
                {social.icon}
              </motion.a>
            ))}
          </div>

          <p className="ft-copyright">
            © {getYear()} Karanks1436. Made with <FaHeart className="ft-heart" /> All rights
            reserved.
          </p>
        </motion.div>
      </div>

      {/* ═══════ watermark ═══════ */}
      <div className="ft-mark" aria-hidden="true" data-plx="0.08">
        KARAN
      </div>

      {/* ═══════ bottom bar ═══════ */}
      <div className="ft-bottombar">
        <p className="ft-bottombar-text">
          Designed &amp; Built by Karan Kumar • Full Stack Developer
        </p>
      </div>

      {/* ═══════ back to top ═══════ */}
      <button
        className={`ft-top ${showTop ? "show" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
        type="button"
      >
        <FiArrowUp />
      </button>
    </footer>
  );
}
